import { useEffect } from 'react';
import Animated, {
  Easing,
  FadeIn,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from 'react-native-reanimated';
import { StyleSheet, Text, View } from 'react-native';

import { colors, radius, type } from '@/theme';

type Props = {
  label: string;
  value: number;
  delay: number;
  /** Chaos is scored the same way but it is not a good thing. */
  danger?: boolean;
};

export function PillarBar({ label, value, delay, danger = false }: Props) {
  const progress = useSharedValue(0);

  useEffect(() => {
    progress.value = withDelay(
      delay,
      withTiming(value, { duration: 760, easing: Easing.out(Easing.cubic) }),
    );
  }, [delay, progress, value]);

  const fill = useAnimatedStyle(() => ({ width: `${progress.value}%` }));

  return (
    <View style={styles.row}>
      <Text style={styles.label}>{label}</Text>
      <View style={styles.track}>
        <Animated.View
          style={[styles.fill, { backgroundColor: danger ? colors.red : colors.gold }, fill]}
        />
      </View>
      <Animated.Text entering={FadeIn.delay(delay + 300).duration(300)} style={styles.value}>
        {value}
      </Animated.Text>
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  label: {
    ...type.label,
    fontSize: 11,
    color: colors.dim,
    width: 104,
  },
  track: {
    flex: 1,
    height: 9,
    borderRadius: radius.pill,
    backgroundColor: colors.cardHi,
    overflow: 'hidden',
  },
  fill: {
    height: '100%',
    borderRadius: radius.pill,
  },
  value: {
    ...type.title,
    fontSize: 17,
    color: colors.text,
    width: 32,
    textAlign: 'right',
  },
});
